import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  NavigationType,
  useLocation,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import { emptyCheck } from "util/function/function";

const Pagenation = ({
  totalData,
  HandlePrevData,
  HandleNextData,
  data,
  setData,
  setSliceData,
  itemsPerPage,
  setLoading,
}) => {
  const maxPageNumbers = 5; // 최대 보여줄 페이지 번호 개수
  const [totalPages, setTotalPages] = useState(0); // 전체 페이지 수
  const [startPage, setStartPage] = useState(0); // 시작 페이지 번호
  const [searchParams] = useSearchParams();
  const prevCountRef = useRef();
  const location = useLocation();
  const navigationType = useNavigationType();

  // 초기 페이지 설정
  const [page, setPage] = useState(() => {
    if (navigationType === "POP") {
      return Number(sessionStorage.getItem(`page_${location.pathname}`) ?? 1);
    }
    return 1; // 기본값
  });

  // 초기 페이지 설정
  const [pageIndex, setPageIndex] = useState(() => {
    if (navigationType === "POP") {
      return Number(
        sessionStorage.getItem(`pageIndex_${location.pathname}`) ?? 1
      );
    }
    return 1; // 기본값
  });

  // 페이지 그룹 변경 시 이전 페이지 그룹 첫번째 번호 저장
  const prevCount = prevCountRef.current;

  useEffect(() => {
    prevCountRef.current = startPage;
  }, [startPage]);

  // 현재 가져온 데이터를 기준으로 페이지 수 계산
  useEffect(() => {
    setTotalPages(Math.ceil(totalData / 5));
  }, [totalData]);

  // 전체 페이지 수 계산
  useEffect(() => {
    setTotalPages(Math.ceil(totalData / itemsPerPage));
  }, [totalData, itemsPerPage]);

  // 현재 페이지 그룹의 시작 페이지 번호 계산
  useEffect(() => {
    setStartPage(Math.floor((page - 1) / maxPageNumbers) * maxPageNumbers + 1);
    sessionStorage.setItem(`page_${location.pathname}`, page);
    sessionStorage.setItem(`pageIndex_${location.pathname}`, pageIndex);
  }, [page, maxPageNumbers]);

  // 현재 페이지 그룹의 마지막 페이지 번호 계산
  const endPage = useMemo(() => {
    return Math.min(startPage + maxPageNumbers - 1, totalPages);
  }, [startPage, maxPageNumbers, totalPages]);

  // 페이지 번호 배열 생성
  // 예를 들어, 현재 페이지가 6이라면, 페이지 번호 배열은 [6, 7, 8, 9, 10]이 되어야 함
  const [pageNumbers, setPageNumbers] = useState([]);

  useEffect(() => {
    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    setPageNumbers(pages);
  }, [startPage, endPage]);

  const onPageChange = (page, index) => {
    setPage(page);
    setPageIndex(index + 1);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  useEffect(() => {
    handlePop();
  }, [startPage]);

  // 새로고침, 뒤로가기시 실행되는 함수
  const handlePop = async () => {
    if (navigationType === "POP") {
      const savedPage = JSON.parse(
        sessionStorage.getItem(`pagination_groupData_${location.pathname}`)
      );

      if (!savedPage) return;

      setData(savedPage);
      handleSliceData(savedPage);
      setLoading(false); // 필요 시
    }
  };

  useEffect(() => {
    handlePageList();
  }, [startPage, searchParams]);

  const handlePageList = async () => {
    const savedPage = JSON.parse(
      sessionStorage.getItem(`pagination_groupData_${location.pathname}`)
    );

    if (startPage === 1) {
      await FirstfetchData();
      return;
    }

    if (prevCount < startPage) {
      if (savedPage) {
        setData(savedPage);
      }
    } else if (prevCount > startPage) {
      // 뒤로 가는 경우
      await PrevfetchData();
    }
  };

  const FirstfetchData = async () => {
    const response = await HandleNextData(null);
    handleSliceData(response);
    setLoading(false);
  };

  const NextfetchData = async (date) => {
    const response = await HandleNextData(date);
    handleSliceData(response);
    setLoading(false);
  };

  const PrevfetchData = async () => {
    let date = null;
    if (startPage === 1) {
      date = null;
    } else {
      date = data[0]?.createDate;
    }
    const response = await HandlePrevData(date);
    handleSliceData(response);
    setLoading(false);
  };

  const handleSliceData = (data) => {
    const num = Number(
      sessionStorage.getItem(`pageIndex_${location.pathname}`)
    );
    if (data && data.length > 0) {
      const currentPageData = data.slice(
        (num - 1) * itemsPerPage,
        num * itemsPerPage
      );
      setSliceData(currentPageData);
      sessionStorage.setItem(
        `pagination_groupData_${location.pathname}`,
        JSON.stringify(data)
      );
    } else {
      setSliceData([]);
    }
  };

  useEffect(() => {
    handleSliceData(data);
  }, [pageIndex]);

  return (
    <>
      <div style={{ display: "flex", gap: "20px", margin: "40px" }}>
        <button
          type="button"
          className="f14_6B7280_W400"
          onClick={() => {
            if (page > 1 && pageNumbers.length > 0) {
              setPage((prev) => prev - 1);
              if (page === startPage) {
                setPageIndex(maxPageNumbers);
              } else {
                setPageIndex((prev) => prev - 1);
              }
            }
          }}
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
            cursor: page > 1 && pageNumbers.length > 0 ? "pointer" : "default",
            color: page > 1 && pageNumbers.length > 0 ? "#6B7280" : "#c3c3c3",
          }}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          <div className="max-md:hidden">이전</div>
        </button>

        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {pageNumbers.length <= 0 && (
            <button
              type="button"
              style={{
                width: "32px",
                height: "32px",
                cursor: "pointer",
                borderRadius: "100%",
                backgroundColor: "#059669",
                color: "#fff",
                fontWeight: "bold",
                cursor: "default",
              }}
              // disabled={number > totalPages ? false : true}
            >
              1
            </button>
          )}
          {pageNumbers.map((number, index) => {
            const isActive = number === page;
            return (
              <button
                type="button"
                key={number}
                onClick={() => onPageChange(number, index)}
                style={{
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  borderRadius: "100%",
                  backgroundColor: isActive && "#059669",
                  color: isActive ? "#fff" : "#4B5563",
                  fontWeight: isActive ? "bold" : "normal",
                  transition: "all 0.2s ease-in-out",
                  userSelect: "none",
                }}
              >
                {number}
              </button>
            );
          })}
        </div>

        <button
          type="button"
          className="f14_6B7280_W400"
          onClick={async () => {
            if (page < totalPages) {
              setPage((prev) => prev + 1);
              if (page === endPage) {
                setPageIndex(1);
                NextfetchData(data[data.length - 1]?.createDate);
              } else {
                setPageIndex((prev) => prev + 1);
              }
            }
          }}
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            cursor: page < totalPages ? "pointer" : "default",
            color: page < totalPages ? "#6B7280" : "#c3c3c3",
          }}
        >
          <div className="max-md:hidden">다음</div>
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>
    </>
  );
};

export default Pagenation;
